import { useEffect, useMemo, useState } from 'react';
import api from '@/api/axiosConfig.js';
import ModalBase from '@/components/Modal/ModalBase.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import {
  evaluateGuideEligibility,
  getGuideAddress,
  getGuideLocality,
  getGuideNumber,
  getGuideRecipient,
  getGuideSearchText,
  mergeGuideSources,
} from '@/pages/Repartos/repartoEligibility.js';

const GUIDE_FETCH_LIMIT = 200;

/** Picks more guides for a reparto that already exists, with the same eligibility rules as a new one. */
export default function AgregarGuiasModal({ isOpen, onClose, onConfirm, sucursalId, excludedGuiaIds = [], busy = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!isOpen || !sucursalId) return undefined;
    let ignore = false;
    setLoading(true);
    setError('');
    setSelected([]);
    api.get('/api/guias', { params: { sucursal_id: sucursalId, limit: GUIDE_FETCH_LIMIT, page: 1 } })
      .then((response) => {
        if (!ignore) setRows(mergeGuideSources(Array.isArray(response?.data?.data) ? response.data.data : []));
      })
      .catch((err) => {
        if (!ignore) {
          setRows([]);
          setError(err?.response?.data?.message || err?.message || 'No se pudieron cargar las guías.');
        }
      })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [isOpen, sucursalId]);

  const excluded = useMemo(() => new Set(excludedGuiaIds.map(Number)), [excludedGuiaIds]);
  const available = useMemo(
    () => rows
      .filter((guide) => !excluded.has(Number(guide.id)) && evaluateGuideEligibility(guide, { sucursalId }).eligible)
      .map((guide) => ({
        id: Number(guide.id),
        number: getGuideNumber(guide),
        recipient: getGuideRecipient(guide),
        address: getGuideAddress(guide),
        locality: getGuideLocality(guide),
        searchText: getGuideSearchText(guide),
      })),
    [excluded, rows, sucursalId],
  );
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? available.filter((guide) => guide.searchText.includes(term)) : available;
  }, [available, search]);

  const toggle = (id) => setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  const confirmLabel = `Agregar ${selected.length} ${selected.length === 1 ? 'guía' : 'guías'}`;

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={onClose}
      labelledBy="modal-agregar-guias-reparto"
      className="modal modal-content w-full max-w-3xl rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-5"
    >
      <div className="space-y-3">
        <h2 id="modal-agregar-guias-reparto" className="text-lg font-semibold">Agregar guías al reparto</h2>
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por guía, destinatario o localidad" />
        {error ? <p className="text-sm text-[rgb(var(--danger))]">{error}</p> : null}
        {loading ? <p className="text-sm text-[rgb(var(--muted))]">Cargando guías…</p> : null}
        {!loading && !error && available.length === 0 ? (
          <p className="text-sm text-[rgb(var(--muted))]">No hay guías disponibles para agregar.</p>
        ) : null}
        {visible.length ? (
          <div className="max-h-80 overflow-y-auto rounded-xl border border-[rgba(var(--border),0.6)]">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-[rgb(var(--muted))]">
                <tr>
                  <th className="px-3 py-2" aria-label="Seleccionar" />
                  <th className="px-3 py-2 text-left">Guía</th>
                  <th className="px-3 py-2 text-left">Destinatario</th>
                  <th className="px-3 py-2 text-left">Domicilio</th>
                  <th className="px-3 py-2 text-left">Localidad</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((guide) => (
                  <tr key={guide.id} className="border-t border-[rgba(var(--border),0.4)]">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Seleccionar ${guide.number}`}
                        checked={selected.includes(guide.id)}
                        onChange={() => toggle(guide.id)}
                      />
                    </td>
                    <td className="px-3 py-2 font-semibold">{guide.number}</td>
                    <td className="px-3 py-2">{guide.recipient}</td>
                    <td className="px-3 py-2">{guide.address || '-'}</td>
                    <td className="px-3 py-2">{guide.locality || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="button" disabled={busy || selected.length === 0} onClick={() => onConfirm(selected)}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </ModalBase>
  );
}
